import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, ConfirmModal, Modal, useModalRequestClose } from '@safetyhub/ui';
import { adminApi, extractApiError, type ReportTemplateCategory } from '../../lib/api';
import { useToastStore } from '../../stores/toast';

/**
 * 관리 모달 안의 편집 행 — 서버 행(id 있음)과 새 분류(id null)를 한 목록으로 다룬다.
 * `sortOrder`는 서버가 마지막으로 알던 값(새 분류는 null) — 저장 시 배열 인덱스와 비교해 다른 것만 PATCH.
 */
export interface CategoryDraftRow {
  /** 서버 id. null = 이번에 추가한 분류(저장 시 POST). */
  id: string | null;
  /** 목록 key용 — 새 분류도 안정된 key가 필요하다(입력 포커스 유지). */
  draftKey: string;
  name: string;
  /** 서버가 알던 이름(새 분류는 ''). 저장 시 다르면 PATCH name. */
  originalName: string;
  /** 서버가 알던 정렬값(새 분류는 null). */
  sortOrder: number | null;
  /** 소속 양식 수 — 삭제 확인 「양식 N개가 미분류로 이동」의 N(새 분류는 0). */
  templateCount: number;
}

/** 저장 1회가 보내는 API 호출 계획 — 순수 함수(`planCategorySave`)의 산출이라 spec이 직접 잰다. */
export interface CategorySavePlan {
  /** 삭제할 서버 행(id·표시명 — 실패 토스트용). */
  deletes: { id: string; name: string }[];
  /** 새로 만들 분류(배열 인덱스 = 최종 정렬 위치). */
  creates: { draftKey: string; name: string; targetSortOrder: number }[];
  /** 기존 행의 이름·정렬 변경 — 둘 다 바뀌면 한 PATCH. */
  updates: { id: string; name?: string; sortOrder?: number; displayName: string }[];
}

/**
 * 편집 결과 → API 호출 계획. 이름은 trim해 비교한다(공백만 바뀐 것은 변경이 아니다).
 * 정렬은 **배열 인덱스가 곧 최종 sortOrder** — 서버가 알던 값과 다른 행만 PATCH한다.
 * 새 분류는 POST 뒤 서버가 목록 끝(max+1)에 붙이므로, 응답의 sortOrder가 목표 인덱스와 다르면
 * 호출부가 한 번 더 PATCH한다(계획엔 targetSortOrder로만 적는다).
 */
export function planCategorySave(
  rows: readonly CategoryDraftRow[],
  deleted: readonly { id: string; name: string }[],
): CategorySavePlan {
  const creates: CategorySavePlan['creates'] = [];
  const updates: CategorySavePlan['updates'] = [];
  rows.forEach((row, index) => {
    const name = row.name.trim();
    if (row.id === null) {
      creates.push({ draftKey: row.draftKey, name, targetSortOrder: index });
      return;
    }
    const patch: { id: string; name?: string; sortOrder?: number; displayName: string } = {
      id: row.id,
      displayName: name || row.originalName,
    };
    if (name !== row.originalName) patch.name = name;
    if (row.sortOrder !== index) patch.sortOrder = index;
    if (patch.name !== undefined || patch.sortOrder !== undefined) updates.push(patch);
  });
  return { deletes: [...deleted], creates, updates };
}

/** 저장 가능 판정 — 빈 이름·중복 이름(trim 뒤 정확 일치)이 있으면 그 행 인덱스를 돌려준다. */
export function findCategoryNameProblems(rows: readonly CategoryDraftRow[]): {
  emptyIndexes: number[];
  duplicateIndexes: number[];
} {
  const emptyIndexes: number[] = [];
  const duplicateIndexes: number[] = [];
  const seen = new Map<string, number>();
  rows.forEach((row, index) => {
    const name = row.name.trim();
    if (!name) {
      emptyIndexes.push(index);
      return;
    }
    const first = seen.get(name);
    if (first !== undefined) {
      if (!duplicateIndexes.includes(first)) duplicateIndexes.push(first);
      duplicateIndexes.push(index);
    } else {
      seen.set(name, index);
    }
  });
  return { emptyIndexes, duplicateIndexes };
}

function toDraftRows(categories: readonly ReportTemplateCategory[]): CategoryDraftRow[] {
  return categories.map((c) => ({
    id: c.id,
    draftKey: c.id,
    name: c.name,
    originalName: c.name,
    sortOrder: c.sortOrder,
    templateCount: c.templateCount,
  }));
}

let draftSeq = 0;

type Props = {
  open: boolean;
  /** 서버 분류 목록(sortOrder·name 순) — 열릴 때 초기값으로 복사한다(열린 동안의 갱신은 반영하지 않는다). */
  categories: readonly ReportTemplateCategory[];
  onClose: () => void;
  /** 저장이 서버에 닿은 뒤(전부 성공이든 일부 실패든) — 호출부가 분류·템플릿 목록을 다시 읽는다. */
  onSaved: () => void;
};

/**
 * 양식 분류(category) 관리 모달 — 추가·이름 변경·위/아래 정렬·삭제를 **모달 안에서 편집하고 「저장」 1회**로
 * diff만 전송한다(행 단위 즉시 PATCH 아님 — 정렬을 몇 번 옮겨도 호출은 최종 위치 기준 한 번).
 * 결정: docs/decisions/2026-09-20-report-template-categories-admin-only.md
 *
 * - 삭제는 서버 행만 확인 모달(danger, 「양식 N개가 미분류로 이동합니다」)을 거친다. 새 분류는 그냥 빠진다.
 * - 저장 순서: 삭제 → 생성 → 갱신(이름·정렬). 어느 호출이 실패하면 **그 행 이름으로 토스트**하고 나머지는
 *   계속 보낸다. 끝나면 성공·실패와 무관하게 `onSaved`(목록 재조회)하고 닫는다 — 일부만 반영된 뒤의
 *   편집본은 서버와 어긋나 있어, 다시 열어 서버 상태에서 이어 가는 편이 안전하다.
 * - dirty 닫기 보호는 base [[Modal]]의 `isDirty`(Esc·오버레이·✕) + footer 취소의 `useModalRequestClose`.
 */
export function TemplateCategoryManageModal({ open, categories, onClose, onSaved }: Props) {
  const showToast = useToastStore((s) => s.showToast);
  const [rows, setRows] = useState<CategoryDraftRow[]>([]);
  const [deleted, setDeleted] = useState<{ id: string; name: string }[]>([]);
  const [initial, setInitial] = useState<string>('');
  const [busy, setBusy] = useState(false);
  // 삭제 확인 대상(서버 행만). null이면 닫힘.
  const [deleteTarget, setDeleteTarget] = useState<CategoryDraftRow | null>(null);
  // 방금 추가한 행에 포커스 — 렌더 뒤 한 번만.
  const focusKeyRef = useRef<string | null>(null);
  const wasOpenRef = useRef(false);

  // 열릴 때만 초기화(닫힘→열림 전환). 열린 동안 categories prop이 바뀌어도 편집본을 덮지 않는다.
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      const draft = toDraftRows(categories);
      setRows(draft);
      setDeleted([]);
      setInitial(serialize(draft, []));
      setDeleteTarget(null);
    }
    wasOpenRef.current = open;
  }, [open, categories]);

  useEffect(() => {
    if (!focusKeyRef.current) return;
    const el = document.querySelector<HTMLInputElement>(
      `input[data-draft-key="${focusKeyRef.current}"]`,
    );
    focusKeyRef.current = null;
    el?.focus();
  });

  const isDirty = serialize(rows, deleted) !== initial;
  const problems = useMemo(() => findCategoryNameProblems(rows), [rows]);
  const canSave =
    isDirty && !busy && problems.emptyIndexes.length === 0 && problems.duplicateIndexes.length === 0;

  const addRow = useCallback(() => {
    draftSeq += 1;
    const draftKey = `new-${draftSeq}`;
    focusKeyRef.current = draftKey;
    setRows((prev) => [
      ...prev,
      { id: null, draftKey, name: '', originalName: '', sortOrder: null, templateCount: 0 },
    ]);
  }, []);

  const rename = useCallback((draftKey: string, name: string) => {
    setRows((prev) => prev.map((r) => (r.draftKey === draftKey ? { ...r, name } : r)));
  }, []);

  const move = useCallback((index: number, delta: -1 | 1) => {
    setRows((prev) => {
      const next = index + delta;
      if (next < 0 || next >= prev.length) return prev;
      const copy = prev.slice();
      [copy[index], copy[next]] = [copy[next], copy[index]];
      return copy;
    });
  }, []);

  const requestRemove = useCallback((row: CategoryDraftRow) => {
    if (row.id === null) {
      setRows((prev) => prev.filter((r) => r.draftKey !== row.draftKey));
      return;
    }
    setDeleteTarget(row);
  }, []);

  const confirmRemove = useCallback(() => {
    if (!deleteTarget || deleteTarget.id === null) return;
    const target = deleteTarget;
    setRows((prev) => prev.filter((r) => r.draftKey !== target.draftKey));
    setDeleted((prev) => [...prev, { id: target.id as string, name: target.originalName }]);
    setDeleteTarget(null);
  }, [deleteTarget]);

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    setBusy(true);
    const plan = planCategorySave(rows, deleted);
    const failures: string[] = [];
    // 실패는 **행 이름으로** 적는다 — 어느 분류가 안 됐는지가 토스트의 값어치다. fetchApi는 4xx를
    // axios 에러로 던지므로(catch), 응답 봉투의 success=false 갈래도 같은 문구로 모은다.
    const fail = (label: string, message: string | undefined, fallback: string) => {
      failures.push(`「${label}」 ${message || fallback}`);
    };
    try {
      for (const d of plan.deletes) {
        try {
          const res = await adminApi.deleteReportTemplateCategory(d.id);
          if (!res.success) fail(d.name, res.error?.message, '삭제 실패');
        } catch (err) {
          fail(d.name, extractApiError(err).message, '삭제 실패');
        }
      }
      for (const c of plan.creates) {
        try {
          const res = await adminApi.createReportTemplateCategory({ name: c.name });
          if (!res.success || !res.data) {
            fail(c.name, res.error?.message, '추가 실패');
            continue;
          }
          // 서버는 목록 끝(max+1)에 붙인다 — 편집 위치와 다르면 정렬만 한 번 더.
          if (res.data.sortOrder !== c.targetSortOrder) {
            const fix = await adminApi.updateReportTemplateCategory(res.data.id, {
              sortOrder: c.targetSortOrder,
            });
            if (!fix.success) fail(c.name, fix.error?.message, '정렬 반영 실패');
          }
        } catch (err) {
          fail(c.name, extractApiError(err).message, '추가 실패');
        }
      }
      for (const u of plan.updates) {
        try {
          const body: { name?: string; sortOrder?: number } = {};
          if (u.name !== undefined) body.name = u.name;
          if (u.sortOrder !== undefined) body.sortOrder = u.sortOrder;
          const res = await adminApi.updateReportTemplateCategory(u.id, body);
          if (!res.success) fail(u.displayName, res.error?.message, '변경 실패');
        } catch (err) {
          fail(u.displayName, extractApiError(err).message, '변경 실패');
        }
      }
    } finally {
      setBusy(false);
    }
    if (failures.length === 0) {
      showToast('분류가 저장되었습니다.', 'success');
    } else {
      showToast(`일부 분류를 저장하지 못했습니다: ${failures.join(' / ')}`, 'error');
    }
    onSaved();
    onClose();
  }, [canSave, rows, deleted, showToast, onSaved, onClose]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="분류 관리"
      size="md"
      isDirty={isDirty}
      closeLocked={busy}
      footer={
        <CategoryModalFooter
          busy={busy}
          isDirty={isDirty}
          canSave={canSave}
          onSave={() => void handleSave()}
          onClose={onClose}
        />
      }
    >
      <div className="space-y-3">
        <p className="text-xs leading-relaxed text-gray-500">
          템플릿 선택 목록을 묶는 분류입니다. 순서가 곧 목록 순서이고, 분류를 정하지 않은 양식은 마지막
          「미분류」에 모입니다. 편집은 <span className="font-medium text-gray-700">저장</span>을 눌러야 반영됩니다.
        </p>
        {rows.length === 0 ? (
          <p className="rounded border border-dashed border-gray-300 px-3 py-4 text-center text-sm text-gray-400">
            분류가 없습니다. 아래 「＋ 분류 추가」로 만드세요.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 rounded border border-gray-200">
            {rows.map((row, index) => {
              const isEmpty = problems.emptyIndexes.includes(index);
              const isDup = problems.duplicateIndexes.includes(index);
              return (
                <li key={row.draftKey} className="flex items-center gap-2 px-2 py-1.5">
                  <span className="w-5 shrink-0 text-right text-xs tabular-nums text-gray-400">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <input
                      type="text"
                      aria-label="분류 이름"
                      data-draft-key={row.draftKey}
                      value={row.name}
                      onChange={(e) => rename(row.draftKey, e.target.value)}
                      placeholder="분류 이름"
                      disabled={busy}
                      aria-invalid={isEmpty || isDup || undefined}
                      className={`w-full rounded border px-2 py-1 text-sm focus:outline-none focus:ring-2 ${
                        isEmpty || isDup
                          ? 'border-red-400 focus:ring-red-200'
                          : 'border-gray-300 focus:ring-brand-200'
                      }`}
                    />
                    {isDup ? (
                      <p className="mt-0.5 text-[11px] text-red-600">같은 이름의 분류가 있습니다.</p>
                    ) : null}
                  </div>
                  <span
                    className="shrink-0 whitespace-nowrap text-[11px] text-gray-400"
                    title={row.id === null ? '저장하면 만들어집니다' : '이 분류에 속한 양식 수'}
                  >
                    {row.id === null ? '새 분류' : `양식 ${row.templateCount}개`}
                  </span>
                  <button
                    type="button"
                    aria-label="위로 이동"
                    title="위로 이동"
                    disabled={busy || index === 0}
                    onClick={() => move(index, -1)}
                    className="rounded border border-gray-300 px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    aria-label="아래로 이동"
                    title="아래로 이동"
                    disabled={busy || index === rows.length - 1}
                    onClick={() => move(index, 1)}
                    className="rounded border border-gray-300 px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    ▼
                  </button>
                  <button
                    type="button"
                    aria-label="삭제"
                    title={
                      row.id === null
                        ? '추가 취소'
                        : `삭제 — 양식 ${row.templateCount}개가 미분류로 이동합니다`
                    }
                    disabled={busy}
                    onClick={() => requestRemove(row)}
                    className="rounded border border-red-200 px-1.5 py-0.5 text-xs text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    🗑
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <button
          type="button"
          onClick={addRow}
          disabled={busy}
          className="w-full rounded border border-dashed border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          ＋ 분류 추가
        </button>
        {deleted.length > 0 ? (
          <p className="text-[11px] text-amber-600">
            저장하면 {deleted.map((d) => `「${d.name}」`).join(', ')} 분류가 삭제되고 소속 양식은 미분류로 이동합니다.
          </p>
        ) : null}
      </div>
      {/* 삭제 확인 — 이 모달 위에 뜨므로 한 층 위(z-[80]). danger: 취소가 default(Enter 오조작 방지). */}
      <ConfirmModal
        open={deleteTarget !== null}
        title="분류 삭제"
        body={
          deleteTarget
            ? `「${deleteTarget.originalName}」 분류를 삭제합니다. 양식 ${deleteTarget.templateCount}개가 미분류로 이동합니다. 양식 자체는 삭제되지 않습니다. (저장을 눌러야 반영됩니다)`
            : undefined
        }
        confirmLabel="삭제"
        danger
        overlayZClassName="z-[80]"
        onConfirm={confirmRemove}
        onClose={() => setDeleteTarget(null)}
      />
    </Modal>
  );
}

/** 편집본의 비교용 직렬화 — 이름(trim)·순서·삭제 목록. 새 분류는 draftKey로 구분. */
function serialize(rows: readonly CategoryDraftRow[], deleted: readonly { id: string }[]): string {
  return JSON.stringify({
    rows: rows.map((r) => [r.id ?? r.draftKey, r.name.trim()]),
    deleted: deleted.map((d) => d.id),
  });
}

/**
 * footer — 취소는 base의 닫기 요청(dirty면 확인 모달)을 타야 하므로 Modal **하위 컴포넌트**로 뺀다
 * (`useModalRequestClose`는 children 안에서만 context를 잡는다).
 */
function CategoryModalFooter({
  busy,
  isDirty,
  canSave,
  onSave,
  onClose,
}: {
  busy: boolean;
  isDirty: boolean;
  canSave: boolean;
  onSave: () => void;
  onClose: () => void;
}) {
  const requestClose = useModalRequestClose();
  return (
    <>
      <span className="mr-auto text-xs text-gray-400">
        {busy
          ? '저장 중…'
          : !isDirty
            ? '변경사항 없음'
            : canSave
              ? '변경사항 있습니다'
              : '이름을 확인해 주세요'}
      </span>
      <Button variant="secondary" onClick={requestClose ?? onClose} disabled={busy}>
        취소
      </Button>
      <Button variant="primary" onClick={onSave} disabled={!canSave}>
        저장
      </Button>
    </>
  );
}
